import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_PREFIX = 'mc-admin:scroll:';
/**
 * Атрибут на скролл-контейнере `<main>` (см. components/layout/layout.tsx).
 * Контент админки скроллится внутри этого контейнера, а НЕ в window.
 */
const SCROLL_CONTAINER_SELECTOR = '[data-scroll-container]';

function getScrollContainer(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.querySelector<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
}

function readScroll(key: string): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
  const parsed = raw === null ? 0 : Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function writeScroll(key: string, value: number): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + key, String(value));
  } catch {
    // игнорируем недоступность хранилища
  }
}

/**
 * Сохраняет и восстанавливает вертикальную позицию скролла страницы-списка
 * по ключу маршрута (`location.pathname`). Привязывается к контейнеру `<main>`
 * с атрибутом `data-scroll-container`.
 *
 * Восстановление делается несколькими попытками после монтирования, т.к.
 * данные таблицы подгружаются асинхронно и высота контента появляется не сразу.
 *
 * Вызывается один раз в корне list-страницы:
 * @example
 * useScrollRestoration();
 */
export function useScrollRestoration(): void {
  const { pathname } = useLocation();
  const keyRef = useRef(pathname);
  keyRef.current = pathname;

  useEffect(() => {
    const container = getScrollContainer();
    if (!container) {
      return;
    }

    const key = pathname;
    const target = readScroll(key);

    // Высота контента появляется асинхронно (после загрузки данных) — пробуем
    // восстановить позицию в нескольких кадрах, пока контент не «дорастёт».
    let cancelled = false;
    const timeoutIds: number[] = [];
    let rafId = 0;
    const tryRestore = (attempt: number) => {
      if (cancelled || target <= 0) {
        return;
      }
      const maxScroll = container.scrollHeight - container.clientHeight;
      container.scrollTop = Math.min(target, Math.max(maxScroll, 0));
      // Если контента ещё мало (не доскроллили до цели) — повторяем
      if (container.scrollTop < target - 1 && attempt < 8) {
        timeoutIds.push(window.setTimeout(() => tryRestore(attempt + 1), 60));
      }
    };
    rafId = window.requestAnimationFrame(() => tryRestore(0));

    const handleScroll = () => {
      writeScroll(keyRef.current, container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      cancelled = true;
      // Финально фиксируем позицию на момент ухода со страницы
      writeScroll(key, container.scrollTop);
      // rAF отменяется через cancelAnimationFrame, setTimeout — через clearTimeout
      window.cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => window.clearTimeout(id));
      container.removeEventListener('scroll', handleScroll);
    };
    // pathname в зависимостях: при смене маршрута переинициализируем
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}
